import React, { useEffect, useRef } from 'react';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { createSvgIcon } from '@mui/material';

import clsx from 'clsx';
import { animated, useSpring } from '@react-spring/web';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import { DiDatabase } from 'react-icons/di';
import LanguageIcon from '@mui/icons-material/Language';
import { treeItemClasses } from '@mui/x-tree-view/TreeItem';
import '../allPages.css';

import { useTreeItem, UseTreeItemParameters } from '@mui/x-tree-view/useTreeItem';
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemRoot,
} from '@mui/x-tree-view/TreeItem';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view/TreeItemDragAndDropOverlay';
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks';
import { fontSize, palette } from '../..';
import { TreeViewItemId } from '@mui/x-tree-view';

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string;
    '--tree-view-bg-color'?: string;
  }
}

const RichTreeViewControl = ({
  list,
  proceedButtonName,
  currentWorkspace,
  handleProceedButtonClick,
  handleDirectClick,
  fileType,
  itemClickedIncanvas,
  selectedFlatFiles,
  expanded,
  setExpanded,
  currentWorkSpaceRef
}: any) => {
  const currentWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const sortedList = React.useMemo(() => {
    return [...list].sort((a: any, b: any) => {
      return a.label.localeCompare(b.label);
    });
  }, [list]);
  proceedButtonName = proceedButtonName || 'Proceed';
  const [lastSelectedItem, setLastSelectedItem] = React.useState<string | null>(null);
  const apiRef = useTreeViewApiRef();

  const handleItemSelectionToggle = (
    event: React.SyntheticEvent<Event | Element> | null,
    itemId: string,
    isSelected: boolean,
  ) => {
    if ((fileType === 'dbcon' || fileType === 'webapi') && isSelected) {
      setLastSelectedItem(itemId);
    }

    if (handleProceedButtonClick && fileType === 'flatfile') {
      handleProceedButtonClick(itemId, sortedList, isSelected);
    }
  };

  const handleExpandedItemsChange = (_: React.SyntheticEvent | null, itemIds: TreeViewItemId[]) => {
    setExpanded?.(itemIds);
  };

  useEffect(() => {
    if (lastSelectedItem && handleDirectClick) {
      handleDirectClick(lastSelectedItem, sortedList);
    }
  }, [lastSelectedItem]);

  useEffect(() => {
    if (!itemClickedIncanvas) return;

    const path = JSON.parse(itemClickedIncanvas);
    if (!path) return;
    path.forEach((itemId: string, index: number) => {
      setTimeout(() => {
        apiRef.current!.setItemExpansion({
          itemId,
          shouldBeExpanded: true,
        });
      }, index * 200);
    });
  }, [itemClickedIncanvas]);

  type FileType = 'dbcon' | 'folder' | 'dataset' | 'flatfile' | 'playbook' | 'webapi';

  function DotIcon() {
    return (
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '70%',
          bgcolor: '#2bb9bb',
          display: 'inline-block',
          verticalAlign: 'middle',
          zIndex: 1,
          mx: 1,
        }}
      />
    );
  }

  const StyledTreeItemRoot = styled(TreeItemRoot)(({ theme }) => ({
    color: theme.palette.grey[400],
    position: 'relative',

    '&.Mui-selected > .MuiTreeItem-content': {
      backgroundColor: 'rgba(43, 185, 187, 0.1)',
    },

    [`& .${treeItemClasses.groupTransition}`]: {
      marginLeft: theme.spacing(3.5),
    },
  }));

  const CustomTreeItemContent = styled(TreeItemContent)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row', // <-- ensure row layout
    borderRadius: theme.spacing(0.7),
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    fontWeight: 500,
    color: '#424242',

    '&:hover': {
      backgroundColor: '#f5f5f5',
    },

    '&.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: 'rgba(43, 185, 187, 0.1)',
      color: '#424242 !important',
    },

    '&.Mui-focused': {
      backgroundColor: 'rgba(43, 185, 187, 0.1)',
    },

    '& .MuiTypography-root': {
      color: '#424242 !important',
    },

    '& .MuiCheckbox-root': {
      color: '#2bb9bb',
      marginRight: theme.spacing(1), // <-- add spacing after checkbox
    },

    '& .MuiCheckbox-root.Mui-checked': {
      color: '#2bb9bb',
    },
  }));

  const AnimatedCollapse = animated(Collapse);

  function TransitionComponent(props: TransitionProps) {
    const style = useSpring({
      to: {
        opacity: props.in ? 1 : 0,
        transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
      },
    });

    return <AnimatedCollapse style={style} {...props} />;
  }

  const StyledTreeItemLabelText = styled(Typography)({
    color: 'inherit',
    fontFamily: 'General Sans',
    fontWeight: 500,
  }) as unknown as typeof Typography;

  interface CustomLabelProps {
    itemId: string;
    children: React.ReactNode;
    icon?: React.ElementType;
    expandable?: boolean;
  }

  function CustomLabel({ itemId, icon: Icon, expandable, children, ...other }: CustomLabelProps) {
    return (
      <TreeItemLabel
        ref={currentWorkspace === itemId || expandable ? currentWorkspaceRef : null}
        {...other}
        sx={{
          display: 'flex',
          alignItems: 'center',
          '&:hover .labelIcon': {
            color: palette.primary.main,
          },
          '.Mui-selected &:hover .labelIcon': {
            color: 'white',
          },
        }}
      >
        {Icon && (
          <Box
            component={Icon}
            className="labelIcon"
            color="inherit"
            sx={{
              mr: 1,
              fontSize: '1.5rem',
              color: 'gray',
              '.Mui-selected &': {
                color: 'white',
              },
              '.Mui-selected &:hover': {
                color: 'white',
              },
            }}
          />
        )}
        <StyledTreeItemLabelText
          variant="body2"
          sx={{
            fontFamily: 'Roboto-Regular',
            fontSize: fontSize.medium,
            color: 'primary.contrastText',
            lineHeight: 'normal',
            '.Mui-selected &': {
              color: 'white',
            },
          }}
        >
          {children}
        </StyledTreeItemLabelText>
        {expandable && <DotIcon />}
      </TreeItemLabel>
    );
  }
  const workspaceFolderWork = createSvgIcon(
    <svg width="89" height="72" viewBox="0 0 89 72" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M71.7145 67L6.97222 67"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M71.7145 67L6.97222 67"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path d="M73 33H22.9088" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M73 33H22.9088" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path
        d="M72.7106 16L37.8494 16"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M72.7106 16L37.8494 16"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path d="M29.881 6L5.97621 6" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M29.881 6L5.97621 6" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M5.9762 66L5.9762 6" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M5.9762 66L5.9762 6" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M72.7106 30V16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M72.7106 30V16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path
        d="M7.96826 62L21.9127 33"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M7.96826 62L21.9127 33"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M37.8493 16L30.8771 7.00001"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M37.8493 16L30.8771 7.00001"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M72.1044 66.7376L83.3408 41.3385C85.1132 37.3322 82.1337 32.8387 77.7534 32.912L72.5 32.9999"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M72.1044 66.7376L83.3408 41.3385C85.1132 37.3322 82.1337 32.8387 77.7534 32.912L72.5 32.9999"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
    </svg>,
    'folderWork',
  );
  const customDatasetIcon = createSvgIcon(
    <svg
      width="90"
      height="100"
      viewBox="0 0 90 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="5" y="9" width="30" height="28" stroke="#808080" strokeWidth="6" />
      <rect
        x="10.9706"
        y="15.7727"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect
        x="10.9706"
        y="25.0454"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect x="5" y="65" width="30" height="28" stroke="#808080" strokeWidth="6" />
      <rect
        x="10.9706"
        y="71.7727"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect
        x="10.9706"
        y="81.0454"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect x="55" y="37" width="30" height="28" stroke="#808080" strokeWidth="6" />
      <rect
        x="60.9706"
        y="43.7727"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect
        x="60.9706"
        y="53.0454"
        width="18.0588"
        height="5.18182"
        fill="#808080"
        stroke="#808080"
      />
      <rect x="38.5" y="19.5" width="29" height="3" fill="#808080" stroke="#808080" />
      <rect x="38.5" y="78.5" width="34" height="3" fill="#808080" stroke="#808080" />
      <rect
        x="68.5"
        y="33.5"
        width="14"
        height="3"
        transform="rotate(-90 68.5 33.5)"
        fill="#808080"
        stroke="#808080"
      />
      <rect
        x="69.5"
        y="77.5"
        width="9"
        height="3"
        transform="rotate(-90 69.5 77.5)"
        fill="#808080"
        stroke="#808080"
      />
    </svg>,
    'DatasetIcon',
  );
  const customFlatFileIcon = createSvgIcon(
    <svg width="78" height="94" viewBox="0 0 78 94" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M23 72H44" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M50 9V25" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path
        d="M68 28H71V22H68V28ZM50 22C48.3431 22 47 23.3431 47 25C47 26.6569 48.3431 28 50 28V22ZM68 22L50 22V28L68 28V22Z"
        fill="#808080"
      />
      <path d="M23 57H55.5038" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M23 42H55.5038" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M11 8H50" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M68 22.0001L51 8.6659" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M10 87H68" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path d="M10 8L10 87" stroke="#808080" strokeWidth="6" strokeLinecap="round" />
      <path
        d="M71 30V27H65V30H71ZM65 87C65 88.6569 66.3431 90 68 90C69.6569 90 71 88.6569 71 87H65ZM65 30V87H71V30H65Z"
        fill="#808080"
      />
    </svg>,
    'flatfile_icon',
  );
  const isExpandable = (reactChildren: React.ReactNode) => {
    if (Array.isArray(reactChildren)) {
      return reactChildren.length > 0 && reactChildren.some(isExpandable);
    }
    return Boolean(reactChildren);
  };
  const playbookIcon = createSvgIcon(
    <svg width="94" height="89" viewBox="0 0 94 89" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M31 39V50" stroke="#808080" strokeWidth="8" strokeLinecap="round" />
      <path d="M55 67L56.9101 77.8329" stroke="#808080" strokeWidth="8" strokeLinecap="square" />
      <path d="M39.9101 68L38 78.8329" stroke="#808080" strokeWidth="8" strokeLinecap="square" />
      <path d="M47 31V50" stroke="#808080" strokeWidth="8" strokeLinecap="round" />
      <path d="M64 80H31" stroke="#808080" strokeWidth="8" strokeLinecap="round" />
      <path d="M64 22V50" stroke="#808080" strokeWidth="8" strokeLinecap="round" />
      <mask id="path-7-inside-1_22_22" fill="white">
        <rect x="3" y="5" width="88" height="61" rx="7" />
      </mask>
      <rect
        x="3"
        y="5"
        width="88"
        height="61"
        rx="7"
        stroke="#808080"
        strokeWidth="16"
        mask="url(#path-7-inside-1_22_22)"
      />
    </svg>,
    'Playbook',
  );
  const getIconFromFileType = (fileType: FileType) => {
    switch (fileType) {
      case 'folder':
        return workspaceFolderWork;
      case 'dbcon':
        return DiDatabase;
      case 'dataset':
        return customDatasetIcon;
      case 'webapi':
        return LanguageIcon;
      case 'flatfile':
        // return SchemaIcon;
        return customFlatFileIcon;
      case 'playbook':
        return playbookIcon;
      default:
        return workspaceFolderWork;
    }
  };

  interface CustomTreeItemProps
    extends Omit<UseTreeItemParameters, 'rootRef'>,
      Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {}

  const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>,
  ) {
    const { id, itemId, label, disabled, children, ...other } = props;

    const {
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getGroupTransitionProps,
      getDragAndDropOverlayProps,
      status,
      publicAPI,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const item = publicAPI.getItem(itemId);
    const showCheckbox = item.fileType === 'flatfile';
    const expandable = Array.isArray(children) ? children.length > 0 : Boolean(children);
    let icon;
    // if (expandable) {
    //   icon = FolderRounded;
    // } else if (item.fileType) {
    icon = getIconFromFileType(item.fileType);
    // }

    return (
      <TreeItemProvider id={itemId} itemId={itemId}>
        <StyledTreeItemRoot {...getRootProps({ ...other, 'data-item-id': itemId })}>
          <CustomTreeItemContent
            {...getContentProps({
              className: clsx('content', {
                'Mui-expanded': status.expanded,
                'Mui-selected': status.selected,
                'Mui-focused': status.focused,
                'Mui-disabled': status.disabled,
              }),
            })}
            sx={{ '& .MuiCheckbox-root': { mr: 0 } }}
          >
            <TreeItemIconContainer {...getIconContainerProps()}>
              <TreeItemIcon status={status} />
            </TreeItemIconContainer>
            {showCheckbox && (
              <TreeItemCheckbox
                {...getCheckboxProps()}
                slotProps={{
                  input: {
                    'data-testid': `${item.label}-checkbox`,
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            )}
            <CustomLabel
              {...getLabelProps({
                icon,
                itemId,
                expandable: expandable && status.expanded,
              })}
            />
            <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
          </CustomTreeItemContent>
          {children && (
            <TransitionComponent
              {...getGroupTransitionProps()}
              onEntered={() => {
                if (currentWorkspaceRef.current) {
                  currentWorkspaceRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              }}
            />
          )}
        </StyledTreeItemRoot>
      </TreeItemProvider>
    );
  });
  return (
    <div id="treeDialog">
      <RichTreeView
        apiRef={apiRef}
        onItemSelectionToggle={handleItemSelectionToggle}
        onExpandedItemsChange={handleExpandedItemsChange}
        items={sortedList}
        checkboxSelection={fileType === 'flatfile'}
        multiSelect={fileType === 'flatfile'}
        selectedItems={selectedFlatFiles}
        expandedItems={expanded}
        sx={{
          height: 'fit-content',
          flexGrow: 1,
          maxWidth: 464,
          overflowY: 'auto',
        }}
        slots={{ item: CustomTreeItem }}
      />
    </div>
  );
};

export default RichTreeViewControl;
